import { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import TrashIcon from "@mui/icons-material/Delete";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";

import { Bucket, BucketAsset, BucketNumberEnum } from "features/trading/buckets";
import {
  MAIN_STAR_COLOR,
  MemberHistoryStatusEnum,
  memberHistoryStatus,
  sortMembers,
} from "./constants";
import { BucketActions } from "./useBucketActions";

// Buckets bigger than this get a search field (a Bucket 3 can hold
// hundreds of assets).
const SEARCH_MIN_MEMBERS = 10;

type MemberActions = Pick<
  BucketActions,
  "toggleMain" | "setIncluded" | "removeAsset"
>;

// Whether the pair's AccountHistory exists — reports can only run for
// members with a green check. The red state carries the generation error.
const MemberHistoryIcon = ({ member }: { member: BucketAsset }) => {
  switch (memberHistoryStatus(member)) {
    case MemberHistoryStatusEnum.READY:
      return (
        <Tooltip title="Account history exists">
          <CheckCircleOutline fontSize="small" color="success" />
        </Tooltip>
      );
    case MemberHistoryStatusEnum.FAILED:
      return (
        <Tooltip title={`History generation failed: ${member.history_error}`}>
          <ErrorOutline fontSize="small" color="error" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Account history not generated yet">
          <HourglassEmpty fontSize="small" color="disabled" />
        </Tooltip>
      );
  }
};

// Whether the account STILL holds the security, sitting left of the history
// icon. A member can legitimately have account history and no longer be a
// holding — the position was sold since — so the two are separate icons
// rather than one combined status.
const MemberHoldingIcon = ({ member }: { member: BucketAsset }) =>
  member.is_held ? (
    <Tooltip title="Still in the account's holdings">
      <CheckCircleOutline
        fontSize="small"
        color="success"
        aria-label="Still held"
      />
    </Tooltip>
  ) : (
    <Tooltip title="No longer in the account's holdings">
      <RemoveCircleOutline
        fontSize="small"
        color="disabled"
        aria-label="Not held"
      />
    </Tooltip>
  );

interface BucketMemberRowProps {
  member: BucketAsset;
  isBucket1: boolean;
  isBucket3: boolean;
  actions: MemberActions;
  isSelected: boolean;
  // Report settings only — the client page's Buckets tab doesn't show it.
  showHoldingStatus: boolean;
  // Row click opens/closes the member's account-history panel (Buckets
  // page only — undefined elsewhere).
  onClick?: (member: BucketAsset) => void;
}

// Memoized: with stable `actions` (useBucketActions memoizes them) only the
// rows whose member actually changed re-render — noticeable on large
// Bucket 3 lists, especially while typing in the search.
const BucketMemberRow = memo(
  ({
    member,
    isBucket1,
    isBucket3,
    actions,
    isSelected,
    showHoldingStatus,
    onClick,
  }: BucketMemberRowProps) => (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      paddingX={1}
      paddingY={0.5}
      onClick={onClick ? () => onClick(member) : undefined}
      sx={{
        ...(isBucket3 && !member.included ? { opacity: 0.45 } : {}),
        ...(onClick ? { cursor: "pointer" } : {}),
        ...(isSelected ? { backgroundColor: "action.selected" } : {}),
      }}
    >
      {isBucket1 && (
        <Tooltip
          title={member.is_main ? "Un-set main asset" : "Make main asset"}
        >
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              actions.toggleMain(member);
            }}
          >
            {member.is_main ? (
              <Star fontSize="small" htmlColor={MAIN_STAR_COLOR} />
            ) : (
              <StarBorder fontSize="small" color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      )}
      {isBucket3 && (
        <Checkbox
          size="small"
          checked={member.included}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) =>
            actions.setIncluded(member, event.target.checked)
          }
        />
      )}
      <Typography
        fontWeight={member.is_main ? 700 : 500}
        color="primary.main"
        minWidth={70}
      >
        {member.symbol}
      </Typography>
      <Typography variant="body2" color="text.secondary" flexGrow={1} noWrap>
        {member.asset__name}
      </Typography>
      {!isBucket3 && showHoldingStatus && <MemberHoldingIcon member={member} />}
      {/* Bucket 3 doesn't use AccountHistory — no status to show. */}
      {!isBucket3 && <MemberHistoryIcon member={member} />}
      {!isBucket3 && (
        <Tooltip title="Remove from bucket">
          <IconButton
            size="small"
            color="error"
            aria-label="Remove asset from bucket"
            onClick={(event) => {
              event.stopPropagation();
              actions.removeAsset(member);
            }}
          >
            <TrashIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ),
);

// Reads as the reason the section exists, not as a warning about the
// members: they stay in the bucket and the user may well have added them
// on purpose. It is accurate — a member whose holdings never overlap the
// report's period is skipped by the report and never calculated
// (services/bucket_report._collect_jobs).
const NOT_HELD_DISCLAIMER =
  "The following securities were not held in the account during the " +
  "period and will thus be disregarded during calculations.";

// Tinted, so the second section reads as its own block rather than as one
// more row: the rule above it is the separator, and this is what makes the
// separator legible as a SECTION break next to a whole column of identical
// row dividers. The request asked for no section headings, so the tint and
// the rule are the whole of the delineation and have to carry it.
const NotHeldDisclaimer = () => (
  <Box
    display="flex"
    alignItems="flex-start"
    gap={1}
    paddingX={1}
    paddingY={1.25}
    sx={{ backgroundColor: "action.hover" }}
  >
    <InfoOutlined fontSize="small" color="disabled" sx={{ marginTop: 0.2 }} />
    <Typography variant="caption" color="text.secondary">
      {NOT_HELD_DISCLAIMER}
    </Typography>
  </Box>
);

interface MemberSearchFieldProps {
  search: string;
  onSearchChange: (value: string) => void;
  matched: number;
  total: number;
}

const MemberSearchField = ({
  search,
  onSearchChange,
  matched,
  total,
}: MemberSearchFieldProps) => (
  <Box display="flex" alignItems="center" gap={1} paddingX={1} paddingBottom={1}>
    <TextField
      fullWidth
      size="small"
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder="Find asset by symbol or name…"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => onSearchChange("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
      {matched} / {total}
    </Typography>
  </Box>
);

const EmptyState = ({ text }: { text: string }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    textAlign="center"
    sx={{ padding: 3 }}
  >
    {text}
  </Typography>
);

interface MemberRowsProps {
  members: BucketAsset[];
  isBucket1: boolean;
  isBucket3: boolean;
  actions: MemberActions;
  showHoldingStatus: boolean;
  selectedAssetId?: number | null;
  onMemberClick?: (member: BucketAsset) => void;
}

// One run of member rows. Separate from the list below so the held /
// not-held split can render two of them inside ONE scroll box, rather than
// giving each section its own scrollbar.
const MemberRows = ({
  members,
  isBucket1,
  isBucket3,
  actions,
  showHoldingStatus,
  selectedAssetId,
  onMemberClick,
}: MemberRowsProps) => (
  <Stack divider={<Divider />}>
    {members.map((member) => (
      <BucketMemberRow
        key={member.id}
        member={member}
        isBucket1={isBucket1}
        isBucket3={isBucket3}
        actions={actions}
        showHoldingStatus={showHoldingStatus}
        isSelected={member.asset === selectedAssetId}
        onClick={onMemberClick}
      />
    ))}
  </Stack>
);

interface BucketMembersListProps {
  bucket: Bucket;
  actions: MemberActions;
  maxHeight?: number;
  // Show the "still in the account's holdings" icon on each row. Off by
  // default: it is a report-settings affordance, and the client page's
  // Buckets tab — which shares these rows — deliberately doesn't carry it.
  showHoldingStatus?: boolean;
  // Stack the members in two sections — held in the report's periods, then
  // the rest under a disclaimer. Set by the report settings only, and only
  // once it has periods: `held_in_period` is False for every member when
  // the bucket list was fetched without them, which would file the whole
  // bucket under "not held" on a screen that never asked about a period.
  splitHeldInPeriod?: boolean;
  // Asset id whose row is highlighted (its history panel is open).
  selectedAssetId?: number | null;
  onMemberClick?: (member: BucketAsset) => void;
}

/**
 * Member rows for a bucket — main-asset star (Bucket 1), include/exclude
 * checkbox (Bucket 3), remove button (Buckets 1/2), and a symbol/name
 * search once the list grows large. Shared between the client-page Buckets
 * tab and the report-settings bucket modals.
 *
 * With `splitHeldInPeriod` the rows are stacked in two sections — held in
 * the report's periods, then, past a separator and a disclaimer, the ones
 * that were not and so cannot reach its calculations.
 */
export const BucketMembersList = ({
  bucket,
  actions,
  maxHeight,
  showHoldingStatus = false,
  splitHeldInPeriod = false,
  selectedAssetId,
  onMemberClick,
}: BucketMembersListProps) => {
  const isBucket1 = bucket.number === BucketNumberEnum.BUCKET_1;
  const isBucket3 = bucket.number === BucketNumberEnum.BUCKET_3;

  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [bucket.id]);

  const sortedMembers = useMemo(
    () => sortMembers(bucket.assets),
    [bucket.assets],
  );

  const showSearch = bucket.assets.length > SEARCH_MIN_MEMBERS;
  const needle = search.trim().toLowerCase();
  const members = useMemo(() => {
    if (!showSearch || !needle) return sortedMembers;
    return sortedMembers.filter(
      (member) =>
        member.symbol.toLowerCase().includes(needle) ||
        member.asset__name.toLowerCase().includes(needle),
    );
  }, [sortedMembers, needle, showSearch]);

  // The split is applied to the SEARCHED rows, so a search narrows both
  // sections at once and a section with no match simply doesn't render —
  // rather than leaving a separator and a disclaimer over nothing.
  const [heldInPeriod, notHeldInPeriod] = useMemo<
    [BucketAsset[], BucketAsset[]]
  >(() => {
    if (!splitHeldInPeriod) return [members, []];
    return [
      members.filter((member) => member.held_in_period),
      members.filter((member) => !member.held_in_period),
    ];
  }, [members, splitHeldInPeriod]);

  if (bucket.assets.length === 0) {
    return <EmptyState text="No assets in this bucket yet." />;
  }

  const rowProps = {
    isBucket1,
    isBucket3,
    actions,
    showHoldingStatus,
    selectedAssetId,
    onMemberClick,
  };

  return (
    <Box>
      {showSearch && (
        <MemberSearchField
          search={search}
          onSearchChange={setSearch}
          matched={members.length}
          total={bucket.assets.length}
        />
      )}
      {members.length === 0 ? (
        <EmptyState text="No assets match your search." />
      ) : (
        // One scroll box around both sections: the separator and the
        // disclaimer scroll with the rows they belong to, as a section
        // heading should, instead of pinning a second scrollbar beside them.
        <Box sx={{ maxHeight, overflowY: maxHeight ? "auto" : undefined }}>
          {heldInPeriod.length > 0 && (
            <MemberRows members={heldInPeriod} {...rowProps} />
          )}
          {notHeldInPeriod.length > 0 && (
            <>
              {/* The separator the two sections are delineated by. Heavier
                  AND darker than the row dividers: at the same colour a 2px
                  rule is indistinguishable from the 1px ones it sits among,
                  which is the whole job it has here. Drawn only when there
                  IS a first section — a period predating the account's
                  positions holds none of its members, and a separator above
                  the first row would be separating nothing. */}
              {heldInPeriod.length > 0 && (
                <Divider
                  sx={{ borderBottomWidth: 2, borderColor: "text.disabled" }}
                />
              )}
              <NotHeldDisclaimer />
              <Divider />
              <MemberRows members={notHeldInPeriod} {...rowProps} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
